import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { ArrowRightIcon, MessageCircleIcon } from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { H2, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import type { UrlOrganisation } from "./types";

const OrganisationChooser = () => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const errorMessage = useAppSelector((state) => state.entry.error);
  const [searchCode, setSearchCode] = useState("");
  const [searching, setSearching] = useState(false);

  const onReceiveOrganisation = useCallback(
    (org: UrlOrganisation) => {
      dispatch(actions.organisations.receiveUrlOrganisation(org));
    },
    [dispatch],
  );
  const onError = useCallback(
    (value: string) => {
      dispatch(actions.entry.updateError(value));
    },
    [dispatch],
  );

  const openChatwoot = useCallback(() => {
    if (window.$chatwoot) {
      window.$chatwoot.toggle();
    }
  }, []);

  const searchByCode = () => {
    setSearching(true);
    onError("");
    api.organisations.getByCode(
      searchCode,
      (organisation) => {
        onReceiveOrganisation(organisation);
        setSearching(false);
      },
      () => {
        onError(t("entry.register.org.error"));
        setSearching(false);
      },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <Modal open={helpModalOpen} onClose={() => setHelpModalOpen(false)}>
        <ModalHeader>What is my organisational code?</ModalHeader>
        <ModalContent>
          <p>
            In order to sign up to {util.appName()} you will need an
            organisational code. This is a unique code that identifies your
            organisation.
          </p>
          <p>
            If you don&apos;t know this code then{" "}
            <span
              style={{ textDecorationLine: "underline", cursor: "pointer" }}
              onClick={() => openChatwoot()}
            >
              click here
            </span>{" "}
            or press the button below to start a chat with us. Tell us which
            organisation you are looking to join and we can support you to get
            started.
          </p>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setHelpModalOpen(false)}>Close</Button>
          <Button onClick={() => openChatwoot()} color="primary">
            <Icon icon={MessageCircleIcon} />
            Speak to us
          </Button>
        </ModalActions>
      </Modal>

      <H2>
        Welcome to {util.appName()}
        <Subheader>{t("entry.register.org.title")}</Subheader>
      </H2>

      {errorMessage && <Message color="orange">{errorMessage}</Message>}

      <div
        style={{
          display: "flex",
          gap: 10,
          flexDirection: theme.sizes.isMobile ? "column" : "row",
        }}
      >
        <Input
          style={{ flex: 1 }}
          label={<Label>.simplydo.co.uk</Label>}
          labelPosition="right"
          name="orgCode"
          placeholder={t("entry.register.org.placeholder")}
          defaultValue={searchCode}
          onKeyDown={(e) => e.keyCode === 13 && searchByCode()}
          onChange={(e) => setSearchCode(e.target.value)}
        />

        <Button
          fluid={theme.sizes.isMobile}
          loading={searching}
          disabled={!searchCode}
          onClick={searchByCode}
          color="primary"
        >
          {t("generic.continue")}
          <Icon icon={ArrowRightIcon} />
        </Button>
      </div>
      <div style={{ textAlign: "right" }}>
        <a
          href="#"
          role="button"
          tabIndex={0}
          onClick={() => setHelpModalOpen(true)}
          onKeyDown={handleClickForKeyboard(() => setHelpModalOpen(true))}
        >
          {t("entry.register.unknownCode")}
        </a>
      </div>
    </div>
  );
};

export default OrganisationChooser;
